import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Leaf, Star } from "lucide-react";
import { getWhatsAppUrl } from "@/config/contact";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const VALUES = [
  { title: "Handmade with Love", desc: "Every single piece is crafted by hand, poured with attention and care. We don't mass-produce — we create.", icon: Heart },
  { title: "Premium Quality", desc: "Only the finest chocolates, freshest flowers, and premium materials find their way into our creations.", icon: Star },
  { title: "Custom Gifting Experts", desc: "Tell us your story, your occasion, your budget — and we'll create something uniquely yours.", icon: Leaf },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-petal-beige" data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#F5F0E8] py-24 px-4 md:px-8 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-petal-gold/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-dancing text-petal-gold text-3xl mb-4">Our Story</motion.p>
            <motion.h1 variants={fadeUp} className="font-playfair text-5xl sm:text-6xl text-petal-olive-text mb-6">
              Every Bouquet<br /><span className="italic text-petal-olive">Tells a Story</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="font-outfit text-base text-petal-muted max-w-2xl mx-auto leading-relaxed">
              Let us be a part of yours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1759563874671-22b56a3bc6d3?w=700&q=80" alt="Our Story"
                className="w-full aspect-[4/3] object-cover border border-petal-border" />
              <div className="absolute -bottom-6 -right-6 bg-petal-olive-text p-8">
                <p className="font-dancing text-petal-gold text-lg">Made with</p>
                <p className="font-playfair text-white text-3xl">Love</p>
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-dancing text-petal-gold text-2xl mb-4">Who We Are</p>
            <h2 className="font-playfair text-4xl text-petal-olive-text mb-6">The Heart Behind<br />Petal Pookie</h2>
            <div className="space-y-4 font-outfit text-sm text-petal-muted leading-relaxed">
              <p>
                Petal Pookie was born from a simple belief: every gift should feel as special as the person receiving it. What started as handcrafted chocolate bouquets for friends and family quickly grew into a beloved gifting brand, known for its premium quality and personal touch.
              </p>
              <p>
                We specialize in customized gifts that speak from the heart — chocolate bouquets wrapped in satin ribbons, rose arrangements that take your breath away, money bouquets for auspicious occasions, and luxury hampers that delight all the senses.
              </p>
              <p>
                Every creation at Petal Pookie is handmade with attention to detail, using only the finest ingredients and materials. We believe that the best gifts aren't bought — they're created with intention and love.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[["500+","Happy Customers"], ["1000+","Gifts Created"], ["100%","Made with Love"]].map(([v, l]) => (
                <div key={l} className="border border-petal-border p-4 text-center">
                  <p className="font-playfair text-3xl text-petal-olive font-semibold">{v}</p>
                  <p className="font-outfit text-xs text-petal-muted uppercase tracking-widest">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 md:px-8 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="font-dancing text-petal-gold text-2xl mb-2">What Sets Us Apart</motion.p>
            <motion.h2 variants={fadeUp} className="font-playfair text-4xl text-petal-olive-text">Our Promise to You</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-8">
            {VALUES.map(v => {
              const Icon = v.icon;
              return (
                <motion.div key={v.title} variants={fadeUp} className="bg-white border border-petal-border p-8 hover:-translate-y-2 transition-transform duration-300">
                  <div className="w-14 h-14 bg-petal-beige-alt border border-petal-border flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-petal-gold" />
                  </div>
                  <h3 className="font-playfair text-xl text-petal-olive-text mb-3">{v.title}</h3>
                  <p className="font-outfit text-sm text-petal-muted leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Instagram */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="font-dancing text-petal-gold text-2xl mb-2">Follow Along</motion.p>
            <motion.h2 variants={fadeUp} className="font-playfair text-4xl text-petal-olive-text mb-4">
              Find Us on Instagram
            </motion.h2>
            <motion.p variants={fadeUp} className="font-outfit text-sm text-petal-muted mb-8 leading-relaxed">
              Follow <strong>Petal Pookie</strong> for daily doses of beautiful creations, behind-the-scenes peeks, and exclusive offers.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://www.instagram.com/petal_pookie.08" target="_blank" rel="noopener noreferrer"
                data-testid="about-instagram-btn" className="btn-primary inline-flex items-center gap-2 justify-center py-4 px-10">
                Follow Petal Pookie
              </a>
              <a href={getWhatsAppUrl("Hi! I'd like to place a custom order.")}
                target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-btn"
                className="btn-gold inline-flex items-center gap-2 justify-center py-4 px-10">
                Chat on WhatsApp
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-petal-olive-text">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-dancing text-petal-gold text-2xl mb-4">Ready to Create?</p>
          <h2 className="font-playfair text-4xl text-petal-beige mb-6">Let's Make Something Beautiful Together</h2>
          <p className="font-outfit text-sm text-petal-border/80 mb-8">
            Browse our collection or reach out to us for a completely personalized gifting experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop" data-testid="about-shop-btn" className="bg-petal-gold text-white px-10 py-4 font-outfit text-sm uppercase tracking-widest hover:bg-petal-gold-dark transition-colors">
              Shop Now
            </Link>
            <Link to="/dashboard" className="border border-petal-border/40 text-petal-beige px-10 py-4 font-outfit text-sm uppercase tracking-widest hover:bg-petal-beige/10 transition-colors">
              My Account
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
